import { TextField, TextFieldProps } from "@mui/material";
import {
  ChangeEvent,
  ChangeEventHandler,
  FC,
  useEffect,
  useState,
} from "react";
import { ErrorOption } from "react-hook-form";
import { convertHexTo6, debounce, toColorInputValue } from "../../utils";
import { ColorPicker } from "../ColorPicker";
import { useFormController } from "../FormController";

export type InputProps = Omit<TextFieldProps, "value" | "onChange" > &
  Partial<{
    noDecimal: boolean;
    noInteger: boolean;
    formControl: boolean;
    value: string;
    allowFormController: boolean;
    onChange: (value: string | boolean | number | null) => void;
    textFieldProps: TextFieldProps;
    trim?: boolean;
  }>;

export const Input: FC<InputProps> = ({
  noDecimal,
  noInteger,
  type,
  value,
  name,
  trim = true,
  onChange,
  allowFormController = true,
  textFieldProps,
  ...props
}) => {
  const controller = useFormController();
  const [inputValue, setInputValue] = useState<string>("");
  const [helperText, setHelperText] = useState<string | undefined>(undefined);

  const setError = (
    error: string | undefined,
    options?: Omit<ErrorOption, "message">
  ) => {
    if (!error) {
      controller?.clearErrors();
    }
    if (controller && allowFormController && name != null) {
      controller.setError(name, { message: error, ...options });
    } else {
      setHelperText(error);
    }
  };

  const onColorPickerChange = (
    event: ChangeEvent<HTMLInputElement>,
    picked?: boolean
  ) => {
    if (picked) {
      let colorValue = event.target.value.toLowerCase();
      colorValue = convertHexTo6(colorValue);
      setInputValue(colorValue);
      onChange?.(colorValue);
    } else {
      debounce(() => {
        let colorValue = event.target.value.toLowerCase();
        colorValue = convertHexTo6(colorValue);
        setInputValue(colorValue);
        onChange?.(colorValue);
      }, 250);
    }
  };

  const onInputChange: ChangeEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = (event) => {
    if (type !== "checkbox") {
      const eventValue = event.target.value;
      setError(undefined);
      let _val: string | number | null = eventValue;
      if (type === "number") {
        if (
          noDecimal &&
          (eventValue.endsWith(".") || eventValue.indexOf(".") !== -1)
        ) {
          return setError("Decimal are not allowed", { type: "validate" });
        } else {
          _val = parseFloat(eventValue);
          if (Number.isNaN(_val)) {
            _val = null;
          }
        }
      } else if (!type || ["tel", "search", "text", "url"].includes(type)) {
        if (noInteger && eventValue.match(/[0-9]/g)) {
          return setError("Numerics are not allowed", { type: "validate" });
        } else if (trim) {
          (_val as string).trim();
        }
      }
      setInputValue(`${_val}`);
      onChange?.(_val);
      if (controller && name != null) {
        controller.setValue(name, _val);
      }
    } else {
      const checked = (event.target as any).checked;
      setInputValue(checked);
    }
  };

  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  const registerResult =
    name != null && controller ? controller.register(name, { ...props }) : null;
  const registerOnChange = registerResult?.onChange;
  const registerRest =
    registerResult == null
      ? {}
      : (() => {
          const { onChange: _omit, ...rest } = registerResult;
          return rest;
        })();

  if (type === "color") {
    const { onChange: _omitTfOnChange, ...colorTextFieldProps } =
      textFieldProps ?? {};
    return (
      <>
        <ColorPicker
          value={toColorInputValue(inputValue)}
          onChange={onColorPickerChange}
          textFieldProps={colorTextFieldProps}
        />
      </>
    );
  } else {
    return (
      <TextField
        error={
          !!helperText ||
          (controller?.formState?.errors &&
            Object.keys(controller?.formState?.errors).length)
        }
        value={inputValue}
        helperText={helperText}
        onChange={(e) => {
          void registerOnChange?.(e);
          void onInputChange(e);
        }}
        type={type}
        {...registerRest}
        {...(props as any)}
      />
    );
  }
};
